import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, File, Video, FileText, Lock, X } from "lucide-react";

interface Resource {
	name: string;
	type: "pdf" | "video" | "document";
	url: string;
}

interface Folder {
	id: string;
	name: string;
	description?: string;
	resources: Resource[];
}

// Helper to get the correct base path for resources
const getResourceUrl = (path: string) => {
	// REACT_APP_RESOURCE_BASE lets the built app point at resources that live
	// somewhere other than alongside it. The resources folder is 80 MB, so the
	// deployed build references the copy already in the repository rather than
	// shipping a second one.
	//
	// Unset (dev, and the GitLab CI build): PUBLIC_URL + /resources, which is
	// /resources in development and /safety-solutions/resources in production.
	const publicUrl = process.env.PUBLIC_URL || "";
	const base = process.env.REACT_APP_RESOURCE_BASE || `${publicUrl}/resources`;
	return `${base}/${path}`;
};

const folders: Folder[] = [
	{
		id: "1",
		name: "Fatigue",
		description: "Explore Optix's fatigue detection and management solutions designed to keep drivers alert and roads safer.",
		resources: [
			{
				name: "Optix Fatigue.pdf",
				type: "pdf",
				url: getResourceUrl("fatigue/Optix Fatigue - April 2026.pdf")
			}
		]
	},
	{
		id: "2",
		name: "Driver Centric Solutions",
		description: "Training, self-coaching, and assessment tools that put drivers at the centre of fleet safety and development.",
		resources: [
			{
				name: "Driver Centric Solutions.pdf",
				type: "pdf",
				url: getResourceUrl("driver-centric-solutions/Driver Centric Solutions - April 2026.pdf")
			},
			{
				name: "Optix Academy Demo.mp4",
				type: "video",
				url: getResourceUrl("driver-centric-solutions/Optix Academy Demo - April 2026.mp4")
			},
			{
				name: "Self-coaching Platform.mp4",
				type: "video",
				url: getResourceUrl("driver-centric-solutions/Self-coaching Platform - April 2026.mp4")
			},
			{
				name: "Understand Me Demo.mp4",
				type: "video",
				url: getResourceUrl("driver-centric-solutions/Understand Me Demo - April 2026.mp4")
			}
		]
	},
	{
		id: "3",
		name: "Optix Intelligence",
		description: "Leverage data-driven insights and analytics to make smarter fleet management decisions.",
		resources: [
			{
				name: "Optix Intelligence.pdf",
				type: "pdf",
				url: getResourceUrl("optix-intelligence/Optix Intelligence - April 2026.pdf")
			}
		]
	},
	{
		id: "4",
		name: "Dynamic Route Optimisation",
		description: "Optimise fleet routes in real time to reduce costs, improve delivery times, and increase operational efficiency.",
		resources: [
			{
				name: "Dynamic Route Optimisation - Vantage.pdf",
				type: "pdf",
				url: getResourceUrl("dynamic-route-optimisation/Dynamic Route Optimisation - Vantage - April 2026.pdf")
			}
		]
	},
	{
		id: "5",
		name: "Global Safety Solutions",
		description: "Learn about Optix's worldwide implementation capabilities and safety solutions across diverse markets.",
		resources: [
			{
				name: "Global Safety Solutions.pdf",
				type: "pdf",
				url: getResourceUrl("global-safety-solutions/Global Safety Solutions - April 2026.pdf")
			}
		]
	},
	{
		id: "6",
		name: "Integrated Solutions",
		description: "See how Optix combines safety telematics with fleet management for a unified, end-to-end solution.",
		resources: [
			{
				name: "Optix Integrated Solutions.pdf",
				type: "pdf",
				url: getResourceUrl("integrated-solutions/Optix Integrated Solutions - April 2026.pdf")
			}
		]
	},
	{
		id: "7",
		name: "Control Tower",
		description: "Centralised fleet visibility and real-time monitoring to manage operations from a single command centre.",
		resources: [
			{
				name: "Optix Control Tower.pdf",
				type: "pdf",
				url: getResourceUrl("control-tower/Optix Control Tower - April 2026.pdf")
			}
		]
	}
];

const API_ENDPOINT = "https://optix-fatigue-reset-zone.waldo-boshoff.workers.dev/api/submit";

// This is a Soft gate triggered at end of content or a back nav on ios
const LeadCaptureModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: { name: string; surname: string; email: string; company: string; designation: string }) => void;
	resourceName: string;
	contentType: "pdf" | "video";
}> = ({ isOpen, onClose, onSubmit, resourceName, contentType }) => {
	const [name, setName] = useState("");
	const [surname, setSurname] = useState("");
	const [email, setEmail] = useState("");
	const [company, setCompany] = useState("");
	const [designation, setDesignation] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const overlayRef = useRef<HTMLDivElement>(null);

	// Reset scroll position and lock body scroll when modal opens
	useEffect(() => {
		if (isOpen) {
			// Lock body scroll
			document.body.style.overflow = 'hidden';
			document.body.style.position = 'fixed';
			document.body.style.width = '100%';
			document.body.style.top = `-${window.scrollY}px`;

			// Reset overlay scroll position
			if (overlayRef.current) {
				overlayRef.current.scrollTop = 0;
			}
		}

		return () => {
			// Restore body scroll
			const scrollY = document.body.style.top;
			document.body.style.overflow = '';
			document.body.style.position = '';
			document.body.style.width = '';
			document.body.style.top = '';
			window.scrollTo(0, parseInt(scrollY || '0') * -1);
		};
	}, [isOpen]);

	const isFormValid = name && surname && email && email.includes("@") && company && designation;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!isFormValid) return;

		setIsSubmitting(true);
		setError(null);

		const formData = {
			name: name.trim(),
			surname: surname.trim(),
			email: email.trim().toLowerCase(),
			company: company.trim(),
			designation: `${designation.trim()} | Resource: ${resourceName} | Source: Optix Resources Request`
		};

		try {
			const response = await fetch(API_ENDPOINT, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify(formData)
			});

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(errorData.error?.message || "Failed to submit form");
			}

			setSubmitted(true);
			onSubmit(formData);

			setTimeout(() => {
				setSubmitted(false);
				setName("");
				setSurname("");
				setEmail("");
				setCompany("");
				setDesignation("");
				onClose();
			}, 3000);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="lead-modal-overlay" ref={overlayRef}>
			<div className="lead-modal-scroll-container">
				<div className="lead-modal">
					<button className="lead-modal-close" onClick={onClose}>
						<X size={24} />
					</button>

				{submitted ? (
					<div className="lead-modal-success">
						<div className="success-checkmark">
							<svg viewBox="0 0 52 52">
								<circle cx="26" cy="26" r="25" fill="none" stroke="#f26722" strokeWidth="2"/>
								<path fill="none" stroke="#f26722" strokeWidth="3" d="M14 27l7 7 16-16"/>
							</svg>
						</div>
						<h2>You're All Set!</h2>
						<p>We'll be in touch with exclusive resources and insights.</p>
					</div>
				) : (
					<>
						<div className="lead-modal-header">
							<div className="lead-modal-badge">
								{contentType === "video" ? "Great choice!" : "Thanks for reading!"}
							</div>
							<h2>Require the Full</h2>
							<h2>Optix Resource Toolkit?</h2>
							<p className="lead-modal-subtitle">
								You will get the "{resourceName}" delivered straight to your inbox.
							</p>
						</div>

						{error && (
							<div className="lead-modal-error">
								{error}
							</div>
						)}

						<form onSubmit={handleSubmit} className="lead-modal-form">
							<div className="lead-input-row">
								<input
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="First name"
									className="lead-input"
									maxLength={50}
									required
								/>
								<input
									type="text"
									value={surname}
									onChange={(e) => setSurname(e.target.value)}
									placeholder="Last name"
									className="lead-input"
									maxLength={50}
									required
								/>
							</div>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="Work email"
								className="lead-input"
								maxLength={100}
								required
							/>
							<input
								type="text"
								value={company}
								onChange={(e) => setCompany(e.target.value)}
								placeholder="Company"
								className="lead-input"
								maxLength={100}
								required
							/>
							<input
								type="text"
								value={designation}
								onChange={(e) => setDesignation(e.target.value)}
								placeholder="Job title / Designation"
								className="lead-input"
								maxLength={100}
								required
							/>
							<button
								type="submit"
								className="lead-submit-btn"
								disabled={isSubmitting || !isFormValid}
							>
								{isSubmitting ? "Sending..." : "Request My Resources"}
							</button>
						</form>

						<button className="lead-skip-btn" onClick={onClose}>
							No thanks, I'll continue browsing
						</button>

						<p className="lead-modal-privacy">
							We respect your privacy. Unsubscribe anytime.
						</p>
					</>
				)}
				</div>
			</div>
		</div>
	);
};

const Dashboard: React.FC<{ onFolderClick: (folder: Folder) => void }> = ({
	onFolderClick
}) => {
	return (
		<div className="dashboard-container">
			<div className="logo-container">
				<img
					src={require("./assets/logo.png")}
					alt="Company Logo"
					className="w-16 h-auto"
				/>
			</div>

			<div className="cta-gradient-wrapper">
				<div className="cta-gradient">
					<div className="cta-gradient-content">
						<h2 className="cta-gradient-heading">Cut Fleet Incidents. Start with a Free Safety Review.</h2>
						<p className="cta-gradient-text">
							We'll assess your current setup and show you exactly where your compliance and safety gaps are... no commitment.
						</p>
					</div>
					<div className="cta-gradient-actions">
						<a
							href="mailto:Stephen.Enslin@optix.co?subject=Free%20Safety%20Review%20Request"
							className="cta-gradient-btn"
						>
							Book a Call with Us
						</a>
						<span className="cta-gradient-hint">Response within 48 hours. No obligation.</span>
					</div>
				</div>
			</div>

			<div className="folders-grid">
				{folders.map((folder) => (
					<button
						key={folder.id}
						onClick={() => onFolderClick(folder)}
						className="folder-card"
					>
						<div className="folder-header">
							<div className="folder-icon">
								<File size={24} />
							</div>
							<h2 className="folder-name">{folder.name}</h2>
						</div>
						<p className="folder-count">
							{folder.resources.length} resource
							{folder.resources.length !== 1 ? "s" : ""}
						</p>
					</button>
				))}
			</div>

			<p className="dashboard-subtitle">
				The files provided are view-only. For copies or further
				assistance, please reach out to{" "}
				Stephen on{" "}
				<a href="mailto:Stephen.Enslin@optix.co">
					<span style={{whiteSpace: 'nowrap'}}>Stephen.Enslin</span>@optix.co
				</a>
			</p>
		</div>
	);
};

const ResourceViewer: React.FC<{ folder: Folder; onBack: () => void }> = ({
	folder,
	onBack
}) => {
	const [selectedResource, setSelectedResource] = useState<Resource | null>(
		null
	);
	const [showLeadModal, setShowLeadModal] = useState(false);
	const [showPdfFinishButton, setShowPdfFinishButton] = useState(false);
	const [capturedLeads, setCapturedLeads] = useState<Set<string>>(new Set());
	const [iosPdfOpened, setIosPdfOpened] = useState<string | null>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const pdfTimerRef = useRef<NodeJS.Timeout | null>(null);

	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

	const getResourceIcon = (type: string) => {
		switch (type) {
			case "video":
				return <Video size={20} />;
			case "pdf":
				return <FileText size={20} />;
			default:
				return <File size={20} />;
		}
	};

	const preventContextMenu = (e: React.MouseEvent) => {
		e.preventDefault();
		return false;
	};

	// Handle video end this triggers a lead capture...
	const handleVideoEnded = () => {
		if (selectedResource && !capturedLeads.has(selectedResource.url)) {
			setShowLeadModal(true);
		}
	};

	const handlePdfFinish = () => {
		if (selectedResource && !capturedLeads.has(selectedResource.url)) {
			setShowLeadModal(true);
		}
	};

	const handleLeadSubmit = (data: { name: string; surname: string; email: string; company: string; designation: string }) => {
		console.log("Lead captured:", data, "for resource:", selectedResource?.name);
		if (selectedResource) {
			setCapturedLeads(prev => new Set(prev).add(selectedResource.url));
		}
	};

useEffect(() => {
		if (selectedResource?.type === "pdf" && !isIOS) {
			setShowPdfFinishButton(false);
			pdfTimerRef.current = setTimeout(() => {
				setShowPdfFinishButton(true);
			}, 15000);
		} else {
			setShowPdfFinishButton(false);
		}

		return () => {
			if (pdfTimerRef.current) {
				clearTimeout(pdfTimerRef.current);
			}
		};
	}, [selectedResource, isIOS]);

	useEffect(() => {
		if (!isIOS || !iosPdfOpened) return;

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && iosPdfOpened) {
				if (!capturedLeads.has(iosPdfOpened)) {
					setTimeout(() => {
						setShowLeadModal(true);
					}, 500);
				}
				setIosPdfOpened(null);
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [isIOS, iosPdfOpened, capturedLeads]);

	useEffect(() => {
		const preventDownload = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "p")) {
				e.preventDefault();
				return false;
			}
		};

		document.addEventListener("keydown", preventDownload);
		return () => document.removeEventListener("keydown", preventDownload);
	}, []);

	return (
		<div className="viewer-container">
			<button onClick={onBack} className="back-button">
				<ArrowLeft size={20} />
				Back to Folders
			</button>

			<div className="folder-info">
				<div className="folder-info-top">
					<h1 className="folder-title">{folder.name}</h1>
					<div className="protection-notice">
						<Lock size={16} />
						<span>Protected resources - View only</span>
					</div>
				</div>
				{folder.description && (
					<p className="folder-description">{folder.description}</p>
				)}
			</div>

			<div className="viewer-layout">
				<div className="resources-sidebar">
					<h2 className="sidebar-title">Resources</h2>
					<div className="resources-list">
						{folder.resources.map((resource, index) => (
							<button
								key={index}
								onClick={() => {
									setSelectedResource(resource);
									if (resource.type === "pdf" && isIOS) {
										setIosPdfOpened(resource.url);
										window.open(resource.url, '_blank');
									}
								}}
								className={`resource-item ${
									selectedResource === resource
										? "active"
										: ""
								}`}
							>
								<div className="resource-icon">
									{getResourceIcon(resource.type)}
								</div>
								<span className="resource-name">
									{resource.name}
								</span>
								<span className={`resource-type-badge ${resource.type}`}>
									{resource.type === "pdf" ? "PDF" : resource.type === "video" ? "Video" : "File"}
								</span>
							</button>
						))}
					</div>
				</div>
				{!(isIOS && selectedResource?.type === "pdf") && (
					<div className="preview-panel">
						{selectedResource ? (
							<>
								<div className="preview-header">
									<span className="preview-header-title">{selectedResource.name}</span>
									<span className={`preview-header-type ${selectedResource.type}`}>
										{selectedResource.type === "pdf" ? "PDF Document" : selectedResource.type === "video" ? "Video" : "File"}
									</span>
								</div>
								<div
									onContextMenu={preventContextMenu}
									className="preview-content"
								>
									{selectedResource.type === "video" ? (
										<video
											ref={videoRef}
											controls
											controlsList="nodownload"
											onContextMenu={preventContextMenu}
											onEnded={handleVideoEnded}
											className="video-player"
											key={selectedResource.url}
											autoPlay
										>
											<source
												src={selectedResource.url}
												type="video/mp4"
											/>
											Your browser does not support the
											video tag.
										</video>
									) : selectedResource.type === "pdf" ? (
										!isIOS ? (
											<div className="pdf-container">
												<iframe
													src={`${selectedResource.url}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
													className="pdf-viewer"
													onContextMenu={
														preventContextMenu
													}
													title={
														selectedResource.name
													}
												/>
												{showPdfFinishButton && !capturedLeads.has(selectedResource.url) && (
													<button
														className="pdf-finish-btn"
														onClick={handlePdfFinish}
													>
														Finished viewing? Get your copy
													</button>
												)}
											</div>
										) : null
									) : (
										<div className="no-preview">
											<File size={64} />
											<p>
												Preview not available for this
												file type
											</p>
										</div>
									)}
								</div>
							</>
						) : (
							<div className="no-preview">
								<div className="no-preview-icon">
									<FileText size={28} />
								</div>
								<p>Select a resource to preview</p>
								<span className="no-preview-hint">Choose from the list on the left</span>
							</div>
						)}
					</div>
				)}
			</div>

			<LeadCaptureModal
				isOpen={showLeadModal}
				onClose={() => setShowLeadModal(false)}
				onSubmit={handleLeadSubmit}
				resourceName={selectedResource?.name || ""}
				contentType={selectedResource?.type === "video" ? "video" : "pdf"}
			/>
		</div>
	);
};

export default function App() {
	const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

	React.useEffect(() => {
		// Handle browser back/forward buttons
		const handlePopState = () => {
			const params = new URLSearchParams(window.location.search);
			const folderId = params.get("folder");

			if (folderId) {
				const folder = folders.find((f) => f.id === folderId);
				setSelectedFolder(folder || null);
			} else {
				setSelectedFolder(null);
			}
		};
		handlePopState();

		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	const handleFolderClick = (folder: Folder) => {
		setSelectedFolder(folder);
		window.history.pushState({}, "", `?folder=${folder.id}`);
	};

	const handleBack = () => {
		setSelectedFolder(null);
		window.history.pushState({}, "", window.location.pathname);
	};

	return (
		<>
			<style>{`
* {
	margin: 0;
	padding: 0;
	box-sizing: border-box;
}

body {
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
		"Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",
		"Helvetica Neue", sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	background: #0f172a;
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
}

video::-internal-media-controls-download-button {
	display: none;
}
video::-webkit-media-controls-enclosure {
	overflow: hidden;
}
video::-webkit-media-controls-panel {
	width: calc(100% + 30px);
}

/* Dashboard Styles */
.dashboard-container {
	min-height: 100vh;
	background: linear-gradient(135deg, #0c0e14 0%, #0d141f 100%);
	padding: 40px 20px;
	max-width: 1500px;
	margin: 0 auto;
}

.logo-container {
	display: flex;
	justify-content: center;
	margin-bottom: 48px;
	height: 128px;
}

.company-logo {
	width: 120px;
	height: auto;
	filter: brightness(1.1);
}

.dashboard-title {
	font-size: 28px;
	font-weight: 700;
	color: #f1f5f9;
	text-align: center;
	margin-bottom: 12px;
	letter-spacing: -0.02em;
}

.dashboard-subtitle {
	font-size: 15px;
	color: #94a3b8;
	text-align: center;
	margin-top: 40px;
	margin-bottom: 0;
	line-height: 1.6;
	max-width: 600px;
	margin-left: auto;
	margin-right: auto;
}

.dashboard-subtitle a {
	color: #f26722;
	text-decoration: none;
}

.dashboard-subtitle a:hover {
	text-decoration: underline;
}

/* Gradient CTA Banner */
.cta-gradient-wrapper {
	max-width: 1200px;
	margin: 24px auto 36px;
	padding: 0 20px;
}

.cta-gradient {
	padding: 40px;
	background: linear-gradient(135deg, #3d1205 40%, #a84010 60%, #f26722 100%);
	border-radius: 14px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 40px;
	position: relative;
	overflow: hidden;
}

.cta-gradient::before {
	content: '';
	position: absolute;
	top: -50%;
	right: -20%;
	width: 400px;
	height: 400px;
	background: radial-gradient(circle, rgba(255, 255, 255, 0.06) 0%, transparent 70%);
	pointer-events: none;
}

.cta-gradient-content {
	flex: 1;
	position: relative;
	z-index: 1;
}

.cta-gradient-heading {
	font-size: 24px;
	font-weight: 700;
	color: #ffffff;
	margin-bottom: 8px;
	letter-spacing: -0.02em;
}

.cta-gradient-text {
	font-size: 15px;
	color: rgba(255, 255, 255, 0.8);
	line-height: 1.6;
	max-width: 400px;
}

.cta-gradient-actions {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	flex-shrink: 0;
	position: relative;
	z-index: 1;
}

.cta-gradient-btn {
	display: inline-block;
	background: #ffffff;
	color: #1a1d23;
	text-decoration: none;
	padding: 16px 32px;
	border-radius: 12px;
	font-size: 16px;
	font-weight: 600;
	transition: all 0.25s ease;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
	white-space: nowrap;
}

.cta-gradient-btn:hover {
	transform: translateY(-2px);
	box-shadow: 0 8px 28px rgba(0, 0, 0, 0.3);
}

.cta-gradient-hint {
	font-size: 13px;
	color: rgba(255, 255, 255, 0.5);
}

@media (max-width: 768px) {
	.cta-gradient {
		flex-direction: column;
		text-align: center;
		padding: 32px 24px;
		gap: 28px;
		align-items: center;
	}

	.cta-gradient-content {
		width: 100%;
	}

	.cta-gradient-text {
		max-width: none;
	}

	.cta-gradient-btn {
		white-space: normal;
		padding: 14px 24px;
		font-size: 15px;
		width: auto;
		align-self: center;
	}
}

.folders-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	gap: 24px;
	max-width: 1200px;
	margin: 0 auto;
	padding: 0 20px;
}

.folder-card {
	background: #1a1d23;
	padding: 24px;
	border-radius: 14px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
	border: 1px solid rgba(255, 255, 255, 0.06);
	border-top: 3px solid #f26722;
	cursor: pointer;
	transition: all 0.25s ease;
	text-align: left;
	position: relative;
	overflow: hidden;
}

.folder-card::after {
	content: '';
	position: absolute;
	top: 0;
	right: 0;
	width: 120px;
	height: 100%;
	background: radial-gradient(ellipse at top right, rgba(242, 103, 34, 0.06) 0%, transparent 70%);
	pointer-events: none;
	transition: opacity 0.25s ease;
}

.folder-card:hover {
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
	transform: translateY(-4px);
	border-color: rgba(242, 103, 34, 0.3);
	border-top-color: #f26722;
	background: #1e2229;
}

.folder-card:hover::after {
	opacity: 1.5;
}

.folder-header {
	display: flex;
	align-items: center;
	margin-bottom: 12px;
}

.folder-icon {
	width: 44px;
	height: 44px;
	background: rgba(242, 103, 34, 0.15);
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 16px;
	color: #f26722;
	transition: all 0.25s ease;
}

.folder-card:hover .folder-icon {
	background: rgba(242, 103, 34, 0.25);
}

.folder-name {
	font-size: 18px;
	font-weight: 600;
	color: #e2e8f0;
	flex: 1;
	line-height: 1.3;
}

.folder-card:hover .folder-name {
	color: #f1f5f9;
}

.folder-count {
	font-size: 13px;
	color: #64748b;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: rgba(255, 255, 255, 0.04);
	padding: 4px 12px;
	border-radius: 6px;
	border: 1px solid rgba(255, 255, 255, 0.06);
}

/* Resource Viewer Styles */
.viewer-container {
	min-height: 100vh;
	background: linear-gradient(135deg, #0c0e14 0%, #0d141f 100%);
	padding: 32px 20px;
	max-width: 1500px;
	margin: 0 auto;
}

.back-button {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: #f1f5f9;
	background: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	margin-bottom: 24px;
	padding: 10px 18px;
	border-radius: 10px;
	transition: all 0.25s ease;
}

.back-button:hover {
	background: rgba(242, 103, 34, 0.1);
	border-color: rgba(242, 103, 34, 0.4);
	color: #f26722;
}

.folder-info {
	background: #1a1d23;
	border-radius: 14px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
	padding: 28px 32px;
	margin-bottom: 28px;
	border: 1px solid rgba(255, 255, 255, 0.06);
	border-top: 3px solid #f26722;
	position: relative;
	overflow: hidden;
}

.folder-info::after {
	content: '';
	position: absolute;
	top: 0;
	right: 0;
	width: 200px;
	height: 100%;
	background: radial-gradient(ellipse at top right, rgba(242, 103, 34, 0.06) 0%, transparent 70%);
	pointer-events: none;
}

.folder-info-top {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
}

.folder-title {
	font-size: 28px;
	font-weight: 700;
	color: #f1f5f9;
	margin: 0;
	letter-spacing: -0.02em;
}

.protection-notice {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	color: #94a3b8;
	background: rgba(255, 255, 255, 0.04);
	padding: 6px 14px;
	border-radius: 8px;
	border: 1px solid rgba(255, 255, 255, 0.06);
}

.folder-description {
	margin-top: 14px;
	padding-top: 14px;
	border-top: 1px solid rgba(255, 255, 255, 0.06);
	font-size: 15px;
	line-height: 1.6;
	color: #94a3b8;
	position: relative;
	z-index: 1;
}

.viewer-layout {
	display: grid;
	grid-template-columns: 320px 1fr;
	gap: 24px;
	max-width: 1400px;
	margin: 0 auto;
	align-items: start;
}

@media (max-width: 1024px) {
	.viewer-layout {
		grid-template-columns: 1fr;
	}
}

.resources-sidebar {
	background: #1a1d23;
	border-radius: 14px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
	padding: 20px;
	border: 1px solid rgba(255, 255, 255, 0.06);
	border-top: 3px solid #f26722;
	position: sticky;
	top: 24px;
}

@media (max-width: 1024px) {
	.resources-sidebar {
		position: static;
	}
}

.sidebar-title {
	font-size: 11px;
	font-weight: 700;
	color: #64748b;
	margin-bottom: 16px;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	padding-bottom: 12px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.resources-list {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.resource-item {
	width: 100%;
	padding: 14px 16px;
	border-radius: 10px;
	background: transparent;
	border: 1px solid transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 14px;
	transition: all 0.2s ease;
}

.resource-item:hover {
	background: rgba(255, 255, 255, 0.04);
}

.resource-item.active {
	background: rgba(242, 103, 34, 0.08);
	border-color: rgba(242, 103, 34, 0.3);
}

.resource-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.05);
	color: #94a3b8;
	flex-shrink: 0;
	transition: all 0.2s ease;
}

.resource-item:hover .resource-icon {
	background: rgba(242, 103, 34, 0.1);
	color: #f26722;
}

.resource-item.active .resource-icon {
	background: rgba(242, 103, 34, 0.15);
	color: #f26722;
}

.resource-name {
	font-size: 14px;
	font-weight: 500;
	color: #cbd5e1;
	white-space: normal;
	word-wrap: break-word;
	overflow-wrap: break-word;
	line-height: 1.4;
	transition: color 0.2s;
}

.resource-item:hover .resource-name {
	color: #f1f5f9;
}

.resource-item.active .resource-name {
	color: #f1f5f9;
	font-weight: 600;
}

.resource-type-badge {
	font-size: 10px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	padding: 3px 8px;
	border-radius: 5px;
	flex-shrink: 0;
	margin-left: auto;
}

.resource-type-badge.pdf {
	background: rgba(239, 68, 68, 0.12);
	color: #f87171;
}

.resource-type-badge.video {
	background: rgba(59, 130, 246, 0.12);
	color: #60a5fa;
}

.preview-panel {
	background: #1a1d23;
	border-radius: 14px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
	border: 1px solid rgba(255, 255, 255, 0.06);
	border-top: 3px solid #f26722;
	overflow: hidden;
}

.preview-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 24px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.06);
	background: rgba(255, 255, 255, 0.02);
}

.preview-header-title {
	font-size: 14px;
	font-weight: 600;
	color: #e2e8f0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.preview-header-type {
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	padding: 4px 10px;
	border-radius: 6px;
	flex-shrink: 0;
}

.preview-header-type.pdf {
	background: rgba(239, 68, 68, 0.12);
	color: #f87171;
}

.preview-header-type.video {
	background: rgba(59, 130, 246, 0.12);
	color: #60a5fa;
}

.preview-content {
	position: relative;
	user-select: none;
}

.video-player {
	width: 100%;
	max-height: 600px;
	display: block;
}

.pdf-container {
	position: relative;
	width: 100%;
	height: 600px;
}

.pdf-viewer {
	width: 100%;
	height: 100%;
	border: transparent;
	background: #0f172a;
	display: block;
}

@media (max-width: 768px) {
	.pdf-container {
		height: calc(100vh - 280px);
		min-height: 400px;
		overflow: hidden;
	}

	.pdf-viewer {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		border: none;
		display: block;
	}
}

.no-preview {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 100px 20px;
	color: #475569;
	gap: 16px;
}

.no-preview-icon {
	width: 72px;
	height: 72px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.03);
	border: 2px dashed rgba(255, 255, 255, 0.08);
	display: flex;
	align-items: center;
	justify-content: center;
	color: #475569;
	margin-bottom: 4px;
}

.no-preview p {
	font-size: 15px;
	color: #64748b;
}

.no-preview .no-preview-hint {
	font-size: 13px;
	color: #475569;
}

/* PDF Finish Button */
.pdf-finish-btn {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 90%;
	max-width: 500px;
	background: linear-gradient(135deg, #f26722 0%, #e55a1b 100%);
	color: white;
	border: none;
	padding: 18px 28px;
	border-radius: 12px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	box-shadow: 0 4px 20px rgba(242, 103, 34, 0.4);
	transition: all 0.3s ease;
	animation: fadeInCenter 0.5s ease-out;
	z-index: 10;
}

.pdf-finish-btn:hover {
	transform: translate(-50%, -50%) scale(1.02);
	box-shadow: 0 6px 25px rgba(242, 103, 34, 0.5);
}

@keyframes fadeInCenter {
	from {
		opacity: 0;
		transform: translate(-50%, -50%) scale(0.95);
	}
	to {
		opacity: 1;
		transform: translate(-50%, -50%) scale(1);
	}
}

/* Lead Capture Modal */
.lead-modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.85);
	z-index: 1000;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
	from { opacity: 0; }
	to { opacity: 1; }
}

.lead-modal-scroll-container {
	min-height: 100%;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 20px;
	padding-top: max(20px, env(safe-area-inset-top, 20px));
	padding-bottom: max(20px, env(safe-area-inset-bottom, 20px));
	box-sizing: border-box;
}

@media (min-height: 700px) {
	.lead-modal-scroll-container {
		align-items: center;
	}
}

.lead-modal {
	background: linear-gradient(145deg, #1e2128 0%, #25282c 100%);
	border-radius: 16px;
	padding: 32px;
	max-width: 440px;
	width: calc(100% - 48px);
	position: relative;
	border: 1px solid rgba(255, 255, 255, 0.1);
	box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
	animation: slideUp 0.4s ease-out;
	flex-shrink: 0;
}

@keyframes slideUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.lead-modal-close {
	position: absolute;
	top: 16px;
	right: 16px;
	background: transparent;
	border: none;
	color: #64748b;
	cursor: pointer;
	padding: 4px;
	transition: color 0.2s;
}

.lead-modal-close:hover {
	color: #f1f5f9;
}

.lead-modal-header {
	text-align: center;
	margin-bottom: 24px;
}

.lead-modal-badge {
	display: inline-block;
	background: rgba(242, 103, 34, 0.15);
	color: #f26722;
	padding: 6px 16px;
	border-radius: 20px;
	font-size: 13px;
	font-weight: 600;
	margin-bottom: 16px;
}

.lead-modal-header h2 {
	color: #f1f5f9;
	font-size: 26px;
	font-weight: 700;
	margin-bottom: 10px;
}

.lead-modal-subtitle {
	color: #94a3b8;
	font-size: 15px;
	line-height: 1.5;
}

.lead-modal-benefits {
	background: rgba(255, 255, 255, 0.03);
	border-radius: 12px;
	padding: 16px;
	margin-bottom: 24px;
}

.benefit-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 8px 0;
	color: #e2e8f0;
	font-size: 14px;
}

.benefit-icon {
	font-size: 18px;
}

.lead-modal-form {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.lead-input-row {
	display: flex;
	gap: 12px;
}

.lead-input-row .lead-input {
	flex: 1;
}

.lead-modal-error {
	background: rgba(239, 68, 68, 0.15);
	border: 1px solid rgba(239, 68, 68, 0.3);
	color: #fca5a5;
	padding: 12px 16px;
	border-radius: 10px;
	font-size: 14px;
	text-align: center;
}

.lead-input {
	width: 100%;
	padding: 14px 16px;
	background: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	color: #f1f5f9;
	font-size: 15px;
	transition: all 0.2s;
}

.lead-input::placeholder {
	color: #64748b;
}

.lead-input:focus {
	outline: none;
	border-color: #f26722;
	background: rgba(242, 103, 34, 0.05);
}

.lead-submit-btn {
	width: 100%;
	padding: 16px;
	background: linear-gradient(135deg, #f26722 0%, #e55a1b 100%);
	border: none;
	border-radius: 10px;
	color: white;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;
	margin-top: 4px;
}

.lead-submit-btn:hover:not(:disabled) {
	transform: translateY(-2px);
	box-shadow: 0 8px 20px rgba(242, 103, 34, 0.4);
}

.lead-submit-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.lead-skip-btn {
	width: 100%;
	padding: 12px;
	background: transparent;
	border: none;
	color: #64748b;
	font-size: 14px;
	cursor: pointer;
	margin-top: 8px;
	transition: color 0.2s;
}

.lead-skip-btn:hover {
	color: #94a3b8;
}

.lead-modal-privacy {
	text-align: center;
	color: #475569;
	font-size: 12px;
	margin-top: 12px;
}

.lead-modal-success {
	text-align: center;
	padding: 20px 0;
}

.success-checkmark {
	width: 64px;
	height: 64px;
	margin: 0 auto 20px;
}

.success-checkmark svg {
	width: 100%;
	height: 100%;
}

.success-checkmark circle {
	animation: circle-draw 0.6s ease-out forwards;
	stroke-dasharray: 160;
	stroke-dashoffset: 160;
}

.success-checkmark path {
	animation: check-draw 0.4s 0.3s ease-out forwards;
	stroke-dasharray: 50;
	stroke-dashoffset: 50;
}

@keyframes circle-draw {
	to { stroke-dashoffset: 0; }
}

@keyframes check-draw {
	to { stroke-dashoffset: 0; }
}

.lead-modal-success h2 {
	color: #f1f5f9;
	font-size: 24px;
	margin-bottom: 8px;
}

.lead-modal-success p {
	color: #94a3b8;
	font-size: 15px;
}

      `}</style>
			{selectedFolder ? (
				<ResourceViewer folder={selectedFolder} onBack={handleBack} />
			) : (
				<Dashboard onFolderClick={handleFolderClick} />
			)}
		</>
	);
}
